import {defineStore} from 'pinia'
import type {OutputEwSuppressionSession, OutputEwFrequencyRange} from '~/models'

interface SuppressionInfo {
  sessionId: number
  directionName: string
  rangeMHz: string
  expectedEndAt: string | undefined
}

// Маржа безпеки для перевірки подавлення (МГц)
const SUPPRESSION_MARGIN_MHZ = 10

function toMHz(range: OutputEwFrequencyRange): { min: number; max: number } {
  const unit = (range.unit || 'MHz').toLowerCase()
  const min = range.min || 0
  const max = range.max || 0
  if (unit === 'ghz') {
    return {min: min * 1000, max: max * 1000}
  }
  return {min, max}
}

function formatRange(range: OutputEwFrequencyRange): string {
  return `${range.min}-${range.max} ${range.unit}`
}

export const useEwSuppressionsStore = defineStore('ewSuppressions', {
  state: () => ({
    sessions: [] as OutputEwSuppressionSession[],
  }),
  getters: {
    activeByZone(): (zoneId: number) => OutputEwSuppressionSession[] {
      return (zoneId: number) =>
        this.sessions.filter(s => s.zoneId === zoneId && s.status === 'active')
    },
    hasActiveForZone(): (zoneId: number) => boolean {
      return (zoneId: number) =>
        this.sessions.some(s => s.zoneId === zoneId && s.status === 'active')
    },
    isFrequencySuppressed(): (zoneId: number, freqMHz: number) => boolean {
      return (zoneId: number, freqMHz: number) => {
        const zoneSessions = this.sessions.filter(s => s.zoneId === zoneId && s.status === 'active')
        return zoneSessions.some(s =>
          (s.frequencyRanges || []).some(r => {
            const {min, max} = toMHz(r)
            return freqMHz >= (min - SUPPRESSION_MARGIN_MHZ) && freqMHz <= (max + SUPPRESSION_MARGIN_MHZ)
          }),
        )
      }
    },
    suppressionInfoForFrequency(): (zoneId: number, freqMHz: number) => SuppressionInfo[] {
      return (zoneId: number, freqMHz: number) => {
        const result: SuppressionInfo[] = []
        const zoneSessions = this.sessions.filter(s => s.zoneId === zoneId && s.status === 'active')
        for (const s of zoneSessions) {
          for (const r of s.frequencyRanges || []) {
            const {min, max} = toMHz(r)
            if (freqMHz >= (min - SUPPRESSION_MARGIN_MHZ) && freqMHz <= (max + SUPPRESSION_MARGIN_MHZ)) {
              result.push({
                sessionId: s.id || 0,
                directionName: s.directionName || '',
                rangeMHz: formatRange(r),
                expectedEndAt: s.expectedEndAt,
              })
            }
          }
        }
        return result
      }
    },
  },
  actions: {
    async load() {
      try {
        const response = await useApi().getEwSuppressions()
        this.sessions = response.sessions || []
      } catch {
        // Якщо у користувача немає доступу до EW suppressions — ігноруємо
        this.sessions = []
      }
    },

    handleCentrifugeNotification(message: {
      type: string
      action: string
      sessionId: number
      zoneId: number
      payload?: OutputEwSuppressionSession
    }) {
      if (!message || message.type !== 'ew_suppression') {
        return
      }

      const {action, sessionId, payload} = message

      switch (action) {
        case 'created':
        case 'activated':
          if (payload) {
            const idx = this.sessions.findIndex(s => s.id === sessionId)
            if (idx >= 0) {
              this.sessions[idx] = payload
            } else {
              this.sessions.push(payload)
            }
          }
          break
        case 'cancelled':
        case 'completed':
          this.sessions = this.sessions.filter(s => s.id !== sessionId)
          break
      }
    },
  },
})